/**
 * GENERATION & IMPACT — workflow guidance derivations.
 *
 * Pure, deterministic projections that power the workbench's guided-workflow
 * surfaces, mirroring the Path-to-Go interaction contract:
 *
 *   `suggestGenerationEquipmentBasis` — the Intent-prefill pattern applied
 *      to the hardest decision on the page (vendor equipment). The suggestion
 *      is NOT an estimate: each catalog screen row already carries a full
 *      solver re-run (`row.result`), so "clears N-1 + reserve" is the exact
 *      oracle outcome, never a linear projection (mirrors the exact band-impact
 *      rule in `lib/pathToGo/nextAction`).
 *
 * No solver logic is duplicated here — this module only reads solved results.
 * Same inputs → same outputs.
 */
import type {
  GenerationImpactResult,
  GenerationImpactRisk,
  GenerationImpactScenarioInput,
} from '../../types/generationImpact';
import type { GenerationEquipmentScreenRow } from './generationEquipmentCatalog';

export type GenerationConstraintFitStatus = 'fits' | 'exceeds' | 'undocumented' | 'not-applicable';

/** The documented site-envelope limits an equipment screen is tested against. */
export type GenerationEquipmentConstraintContext = Pick<GenerationImpactScenarioInput,
  | 'availableGasCapacityDthPerDay'
  | 'availableWaterConsumptionGalPerDay'
  | 'ccsExportCapacityTonnesPerYear'
  | 'ccs'
  | 'receptors'>;

export interface GenerationEquipmentConstraintFit {
  id: 'gas-capacity' | 'water-capacity' | 'co2-export' | 'receptor-noise';
  label: string;
  status: GenerationConstraintFitStatus;
  /** Exact solver numbers (never re-derived thresholds). */
  detail: string;
}

const fitNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);

/**
 * Project one equipment screen result onto the documented site envelope.
 * Statuses come from the solver's own typed risks (the solver stays the only
 * oracle for every threshold); the numbers are quoted from the same result for
 * the detail strings. Pure and deterministic.
 */
export function deriveGenerationEquipmentConstraintFits(
  result: GenerationImpactResult,
  context: GenerationEquipmentConstraintContext,
): readonly GenerationEquipmentConstraintFit[] {
  const risk = (id: GenerationImpactRisk['id']): GenerationImpactRisk | undefined =>
    result.risks.find((candidate) => candidate.id === id);

  const fits: GenerationEquipmentConstraintFit[] = [];

  const gasRisk = risk('gas-capacity-mismatch');
  const peakDthPerDay = result.gas.peakDthPerDay;
  const gasLimit = context.availableGasCapacityDthPerDay;
  fits.push(peakDthPerDay <= 0
    ? { id: 'gas-capacity', label: 'Firm gas', status: 'not-applicable', detail: 'No pipeline gas demand for this technology.' }
    : gasRisk?.severity === 'red' && gasLimit != null
      ? { id: 'gas-capacity', label: 'Firm gas', status: 'exceeds', detail: `Peak nomination ${fitNumber(peakDthPerDay)} Dth/day vs ${fitNumber(gasLimit)} documented (+${fitNumber(peakDthPerDay - gasLimit)}).` }
      : gasRisk
        ? { id: 'gas-capacity', label: 'Firm gas', status: 'undocumented', detail: `Peak nomination ${fitNumber(peakDthPerDay)} Dth/day; firm capacity is not documented.` }
        : { id: 'gas-capacity', label: 'Firm gas', status: 'fits', detail: `Peak nomination ${fitNumber(peakDthPerDay)} Dth/day vs ${fitNumber(gasLimit ?? 0)} documented (${fitNumber((gasLimit ?? 0) - peakDthPerDay)} headroom).` });

  const waterRisk = risk('water-source-mismatch');
  const totalWater = result.water.totalConsumptionGalPerDay ?? null;
  const waterLimit = context.availableWaterConsumptionGalPerDay;
  fits.push(waterRisk?.severity === 'red' && totalWater != null && waterLimit != null
    ? { id: 'water-capacity', label: 'Water', status: 'exceeds', detail: `Peak consumption ${fitNumber(totalWater)} gal/day vs ${fitNumber(waterLimit)} documented (+${fitNumber(totalWater - waterLimit)}).` }
    : waterRisk
      ? { id: 'water-capacity', label: 'Water', status: 'undocumented', detail: waterLimit == null ? `Generation consumption ${fitNumber(result.water.generationConsumptionGalPerDay)} gal/day; water-source capacity is not documented.` : 'Total consumption needs the data-center cooling basis before the documented limit can be screened.' }
      : { id: 'water-capacity', label: 'Water', status: 'fits', detail: `Peak consumption ${fitNumber(totalWater ?? 0)} gal/day vs ${fitNumber(waterLimit ?? 0)} documented (${fitNumber((waterLimit ?? 0) - (totalWater ?? 0))} headroom).` });

  const co2Risk = risk('ccs-export-uncertainty');
  const captured = result.emissions.capturedCo2TonnesPerYear;
  const co2Limit = context.ccsExportCapacityTonnesPerYear;
  fits.push(!context.ccs.enabled
    ? { id: 'co2-export', label: 'CO₂ export', status: 'not-applicable', detail: 'CCS is disabled for this scenario.' }
    : co2Risk?.severity === 'red' && co2Limit != null
      ? { id: 'co2-export', label: 'CO₂ export', status: 'exceeds', detail: `Captured ${fitNumber(captured)} t/y vs ${fitNumber(co2Limit)} documented (+${fitNumber(captured - co2Limit)}).` }
      : co2Risk
        ? { id: 'co2-export', label: 'CO₂ export', status: 'undocumented', detail: `Captured ${fitNumber(captured)} t/y; export route or annual capacity is not confirmed.` }
        : { id: 'co2-export', label: 'CO₂ export', status: 'fits', detail: `Captured ${fitNumber(captured)} t/y vs ${fitNumber(co2Limit ?? 0)} documented (${fitNumber((co2Limit ?? 0) - captured)} headroom).` });

  const noiseRisk = risk('receptor-noise-exceedance');
  const worstMargin = result.noise.reduce<number | null>((worst, row) => (
    row.marginDb == null ? worst : worst == null ? row.marginDb : Math.min(worst, row.marginDb)
  ), null);
  fits.push(noiseRisk?.severity === 'red'
    ? { id: 'receptor-noise', label: 'Receptor noise', status: 'exceeds', detail: worstMargin == null ? 'A screening receptor exceeds its threshold.' : `Worst receptor margin ${worstMargin.toFixed(1)} dB below threshold.` }
    : noiseRisk
      ? { id: 'receptor-noise', label: 'Receptor noise', status: 'undocumented', detail: context.receptors.length === 0 ? 'No screening receptor is documented.' : 'Receptor screening is indeterminate — vendor source data or a threshold is missing.' }
      : { id: 'receptor-noise', label: 'Receptor noise', status: 'fits', detail: worstMargin == null ? 'All documented receptors pass.' : `Worst receptor margin ${worstMargin.toFixed(1)} dB of headroom.` });

  return fits;
}

/**
 * One row of the Step 2 demand-vs-supply ledger: the computed demand the
 * solver produced, the documented supply limit it is screened against, and
 * the fit status. A thin reshaping of `deriveGenerationEquipmentConstraintFits`
 * so this card can never disagree with the Step 3 chips or the risk register.
 */
export interface SiteEnvelopeLedgerRow {
  id: 'gas-capacity' | 'water-capacity' | 'co2-export';
  label: string;
  /** Solver-quoted demand (null when the total cannot be formed yet). */
  demandValue: number | null;
  demandUnit: string;
  /** Why the demand is incomplete, when it is (e.g. missing cooling basis). */
  demandCaveat: string | null;
  /** The documented supply limit, or null when undocumented. */
  supplyValue: number | null;
  status: GenerationConstraintFitStatus;
  /** The fit's exact solver-quoted detail string, verbatim. */
  headroomDetail: string;
}

export function deriveSiteEnvelopeLedger(
  result: GenerationImpactResult,
  context: GenerationEquipmentConstraintContext,
): readonly SiteEnvelopeLedgerRow[] {
  const fits = deriveGenerationEquipmentConstraintFits(result, context);
  const fit = (id: SiteEnvelopeLedgerRow['id']) => fits.find((candidate) => candidate.id === id)!;

  const totalWater = result.water.totalConsumptionGalPerDay ?? null;
  return [
    {
      id: 'gas-capacity',
      label: 'Firm gas',
      demandValue: result.gas.peakDthPerDay,
      demandUnit: 'Dth/day',
      demandCaveat: null,
      supplyValue: context.availableGasCapacityDthPerDay ?? null,
      status: fit('gas-capacity').status,
      headroomDetail: fit('gas-capacity').detail,
    },
    {
      id: 'water-capacity',
      label: 'Water',
      // The incomplete fallback must be the SAME figure the utility-request
      // composer asks for (generation + CCS), or the card and the ask beside
      // it quote two different water demands for one scenario.
      demandValue: totalWater
        ?? result.water.generationConsumptionGalPerDay + result.water.ccsConsumptionGalPerDay,
      demandUnit: 'gal/day',
      demandCaveat: totalWater == null
        ? 'Generation + CCS only — the facility cooling basis is needed to complete the total.'
        : null,
      supplyValue: context.availableWaterConsumptionGalPerDay ?? null,
      status: fit('water-capacity').status,
      headroomDetail: fit('water-capacity').detail,
    },
    {
      id: 'co2-export',
      label: 'CO₂ export',
      demandValue: result.emissions.capturedCo2TonnesPerYear,
      demandUnit: 't/y',
      demandCaveat: null,
      supplyValue: context.ccsExportCapacityTonnesPerYear ?? null,
      status: fit('co2-export').status,
      headroomDetail: fit('co2-export').detail,
    },
  ];
}

export type SiteLimitDocumentationState = 'documented' | 'requested' | 'undocumented' | 'not-applicable';

/**
 * The shared documentation predicate behind the Step 2 checklist chips.
 * `requested` means a utility data request is recorded but the limit is still
 * undocumented — it never satisfies the stage gate (`constraintsDocumented`),
 * which keeps requiring a documented value.
 */
export function deriveSiteLimitDocumentation(
  input: Pick<GenerationImpactScenarioInput,
    | 'availableGasCapacityDthPerDay'
    | 'availableWaterConsumptionGalPerDay'
    | 'ccsExportCapacityTonnesPerYear'
    | 'ccs'
    | 'receptors'
    | 'resourceLimitRequests'>,
  isGasTechnology: boolean,
): Record<'gas' | 'water' | 'co2' | 'receptor', SiteLimitDocumentationState> {
  const limitState = (
    applicable: boolean,
    documented: boolean,
    requested: boolean,
  ): SiteLimitDocumentationState => !applicable
    ? 'not-applicable'
    : documented
      ? 'documented'
      : requested
        ? 'requested'
        : 'undocumented';
  const requests = input.resourceLimitRequests ?? {};
  return {
    gas: limitState(isGasTechnology, input.availableGasCapacityDthPerDay != null, requests.gas != null),
    water: limitState(true, input.availableWaterConsumptionGalPerDay != null, requests.water != null),
    co2: limitState(
      isGasTechnology && input.ccs.enabled,
      input.ccsExportCapacityTonnesPerYear != null,
      requests.co2 != null,
    ),
    receptor: limitState(true, input.receptors.length > 0, false),
  };
}

export interface GenerationEquipmentSuggestion {
  catalogId: string;
  manufacturer: string;
  model: string;
  trainConfiguration: string;
  siteNetMWPerBlock: number;
  /** True — enforced by selection — both duty and reserve clear in the exact solver screen. */
  clearsContingency: true;
  clearsReserve: true;
  /** ≤160-char provenance rationale for the accept affordance's tooltip. */
  rationale: string;
  /** Present when a documented site envelope was supplied for the ranking. */
  constraintFits?: readonly GenerationEquipmentConstraintFit[];
}

function capacityVerdicts(result: GenerationImpactResult): { duty: boolean; reserve: boolean } {
  const capacity = result.capacity;
  return {
    duty: (capacity.contingencyVerdict ?? capacity.nMinusOneVerdict) === 'pass',
    reserve: (capacity.contingencyReserveVerdict ?? capacity.nMinusOneReserveVerdict) === 'pass',
  };
}

/**
 * The one-click equipment suggestion: the least-oversized catalog envelope
 * whose EXACT solver screen clears both the active contingency duty and the
 * reserve requirement. Returns null when no candidate fully clears — a partial
 * fix is never suggested (honesty over helpfulness).
 *
 * Selection is deterministic. Full duty+reserve passes only, then (when a
 * documented site envelope is supplied) fewest exceeded documented limits,
 * fewest undocumented limits, smallest site-net MW per block (least oversize),
 * catalog order as the tie-break. A constraint exceedance never blanks the
 * suggestion — capacity stays the only hard gate; exceedances are disclosed.
 */
export function suggestGenerationEquipmentBasis(
  rows: readonly GenerationEquipmentScreenRow[],
  criterionLabel: string,
  constraintContext?: GenerationEquipmentConstraintContext,
): GenerationEquipmentSuggestion | null {
  // Confirmation-locked rows are never suggested: the one-click apply would
  // throw (`applyGenerationEquipmentEnvelope` refuses unconfirmed envelopes).
  const passing = rows.filter((row) => {
    if (row.confirmationRequired) return false;
    const verdicts = capacityVerdicts(row.result);
    return verdicts.duty && verdicts.reserve;
  });
  if (passing.length === 0) return null;
  const scored = passing.map((row) => ({
    row,
    fits: constraintContext
      ? deriveGenerationEquipmentConstraintFits(row.result, constraintContext)
      : null,
  }));
  const statusCount = (
    fits: readonly GenerationEquipmentConstraintFit[] | null,
    status: GenerationConstraintFitStatus,
  ): number => fits?.filter((fit) => fit.status === status).length ?? 0;
  // Array.prototype.sort is stable, so equal keys preserve catalog order.
  const best = [...scored].sort((left, right) => (
    statusCount(left.fits, 'exceeds') - statusCount(right.fits, 'exceeds')
    || statusCount(left.fits, 'undocumented') - statusCount(right.fits, 'undocumented')
    || left.row.siteNetMWPerBlock - right.row.siteNetMWPerBlock
  ))[0]!;
  const exceeded = best.fits?.filter((fit) => fit.status === 'exceeds') ?? [];
  const constraintNote = best.fits == null
    ? ''
    : exceeded.length > 0
      ? ` Exceeds the documented ${exceeded.map((fit) => fit.label.toLowerCase()).join(' and ')} limit${exceeded.length === 1 ? '' : 's'}.`
      : best.fits.some((fit) => fit.status === 'undocumented')
        ? ' Some site-envelope limits remain undocumented.'
        : ' Fits every documented site-envelope limit.';
  const gapNote = best.row.unavailableFields.length > 0
    ? ` ${best.row.unavailableFields.length} catalog field${best.row.unavailableFields.length === 1 ? '' : 's'} unavailable.`
    : '';
  return {
    catalogId: best.row.catalogId,
    manufacturer: best.row.manufacturer,
    model: best.row.model,
    trainConfiguration: best.row.trainConfiguration,
    siteNetMWPerBlock: best.row.siteNetMWPerBlock,
    clearsContingency: true,
    clearsReserve: true,
    rationale: `${best.row.manufacturer} ${best.row.model} (${best.row.trainConfiguration}) clears the ${criterionLabel} duty and reserve in the exact solver screen at ${best.row.siteNetMWPerBlock.toFixed(1)} MW site net per block.${constraintNote}${gapNote}`,
    ...(best.fits ? { constraintFits: best.fits } : {}),
  };
}
